import { useState } from 'react'
import { api, ApiError } from '../lib/api'
import type { ApiKeyCreated, ApiKeyCreateRequest } from '../types/api'

// Same limits as ApiKeyCreateRequest (schemas.py).
const NAME_MAX = 120
const DESCRIPTION_MAX = 500

interface CreateApiKeyModalProps {
  onClose: () => void
  onSuccess: (key: ApiKeyCreated) => void
}

export default function CreateApiKeyModal({ onClose, onSuccess }: CreateApiKeyModalProps) {
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [expiresInDays, setExpiresInDays] = useState(365)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!displayName.trim()) {
      setError('Display name is required')
      return
    }

    try {
      setLoading(true)
      setError(null)
      setNameError(null)

      const request: ApiKeyCreateRequest = {
        display_name: displayName.trim(),
        description: description.trim() || null,
        expires_in_days: expiresInDays,
      }

      const createdKey = await api.createApiKey(request)
      onSuccess(createdKey)
    } catch (err) {
      if (err instanceof ApiError && err.error === 'name_taken') {
        setNameError(err.message)
      } else if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Failed to create API key')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-modal w-full max-w-md p-6">
          <div className="absolute top-4 right-4">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Create API Key
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                Display Name *
              </label>
              <input
                type="text"
                id="displayName"
                value={displayName}
                onChange={(e) => { setDisplayName(e.target.value); setNameError(null) }}
                className="input mt-1"
                placeholder="e.g. Mideye Server Stockholm"
                maxLength={NAME_MAX}
                aria-invalid={nameError ? true : undefined}
                aria-describedby="displayNameHelp"
              />
              <div id="displayNameHelp" className="mt-1 flex justify-between gap-2 text-xs">
                <span className={nameError ? 'text-red-700' : 'text-gray-500'}>
                  {nameError ?? 'Name it after the installation that will use it.'}
                </span>
                <span className="shrink-0 text-gray-500">{displayName.length} / {NAME_MAX}</span>
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="input mt-1"
                placeholder="Optional description of the API key's purpose"
                maxLength={DESCRIPTION_MAX}
              />
              <p className="mt-1 text-right text-xs text-gray-500">{description.length} / {DESCRIPTION_MAX}</p>
            </div>

            <div>
              <label htmlFor="expiresInDays" className="block text-sm font-medium text-gray-700">
                Expires In
              </label>
              <select
                id="expiresInDays"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                className="input mt-1"
              >
                <option value={30}>30 days</option>
                <option value={45}>45 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
                <option value={180}>6 months</option>
                <option value={365}>1 year</option>
                <option value={730}>2 years</option>
              </select>
            </div>

            <p className="text-xs text-gray-600">
              One key per installation. Servers in a cluster that share one database count as one installation.
            </p>

            {error && (
              <div className="rounded-md bg-red-50 p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-accent border border-transparent rounded-lg hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
